// src/store/ui
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { getProperty as getProp, setProperty as setProp } from 'dot-prop'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '~/lib/constants'
import initialState from './initial'

const logger = console.scope('ui', true)

// Slice

export const uiSlice = createSlice({
	name: 'ui',
	initialState,
	reducers: {
		setPending: (s, { payload }) => {
			s.pending = payload
		},
		setTextures: (s, { payload }) => {
			s.textures = { ...s.textures, ...payload }
		},
		setMouseDown: (s, { payload }) => {
			window.mouse_down = s.mouse_down = !!payload
		},
		setSettingsPage: (s, { payload }) => {
			s.modal = `settings/${payload}`
		},
		requestConfirm: (s, { payload }) => {
			const { id, title, message, action, noAction, yesLabel, noLabel, confirmType, destructive } = payload
			if (!id || !title || !message) {
				logger.error('Invalid confirm payload')
				return
			}
			s.confirm = { id, title, message, action, noAction, yesLabel, noLabel, confirmType, destructive }
		},
		clearConfirm: s => {
			s.confirm = null
		},
		requestPrompt: (s, { payload }) => {
			const { id, title, message, inputValue, inputPlaceholder, action, destructive } = payload
			if (!id || !title) {
				logger.error('Invalid prompt payload')
				return
			}
			s.prompt = { id, title, message, inputValue, inputPlaceholder, action, destructive }
		},
		setPromptInput: (s, { payload }) => {
			if (s.prompt) {
				s.prompt.inputValue = payload
			}
		},
		clearPrompt: s => {
			s.prompt = null
		},
		setPage: (s, { payload }) => {
			s.page = payload
		},
		setDialog: (s, { payload }) => {
			s.dialog = payload
		},
		setShareDialog: (s, { payload }) => {
			s.shareDialog = payload
		},
		setUIProperty: (s, { payload }) => {
			const [k, v] = payload
			setProp(s, k, v)
		},
		toggleUIProperty: (s, { payload }) => {
			const value = getProp(s, payload)
			setProp(s, payload, !value)
			if (payload.startsWith('show.')) {
				const panelId = payload.slice(5)
				delete s.panels.collapsed[panelId]
			}
		},
		enforceKiosk: s => {
			s.show.ui = false
			s.show.output = true
			s.show.source = false
		},
		setRename: (s, { payload }) => {
			s.rename = payload
		},
		// Error queue - payload must be serializable data only (no functions)
		pushError: (s, { payload }) => {
			const { code, title, message } = payload
			if (typeof payload.onOkay === 'function' || typeof payload.onCancel === 'function') {
				logger.error('pushError: Functions not allowed in payload')
				return
			}
			s.errors.push({ code, title, message })
		},
		closeError: s => {
			s.errors.shift()
		},
		setCSSPortrait: (s, { payload }) => {
			s.isCSSPortrait = payload
		},
		setPanelCollapsed: (s, { payload: { id, collapsed } }) => {
			s.panels.collapsed[id] = collapsed
			if (s.panels.partial) delete s.panels.partial[id]
		},
		// Keeps a partly expanded section's rows through its close: dropping them grows it to every row as it shrinks. Opening it clears them.
		collapsePanels: (s, { payload: ids }) => {
			for (const id of ids) s.panels.collapsed[id] = true
		},
		// Opens each section showing only the param paths it maps to; its arrow then shows the rest.
		expandPanelsPartly: (s, { payload: paths }) => {
			s.panels.partial ??= {}
			for (const [id, shown] of Object.entries(paths)) {
				s.panels.collapsed[id] = false
				s.panels.partial[id] = shown
			}
		},
		setPanelZoom: (s, { payload: { context, mode, value } }) => {
			s.panels.zoom ??= {}
			s.panels.zoom[context] = { ...s.panels.zoom[context], [mode]: value }
		},
		toggleFold: (s, { payload: { id, open } }) => {
			const next = open ?? !s.folds[id]
			if (next) s.folds[id] = true
			else delete s.folds[id]
		},
		setPanelSize: (s, { payload: { id, ratio } }) => {
			if (ratio == null) {
				delete s.panels.sizes[id]
			} else {
				s.panels.sizes[id] = ratio
			}
		},
		setDockLayout: (s, { payload }) => {
			s.dock.layout = payload ?? null
		},
		setDockPlacement: (s, { payload: { id, placement } }) => {
			if (placement == null) delete s.dock.placements[id]
			else s.dock.placements[id] = placement
		},
		setDockCollapsed: (s, { payload }) => {
			s.dock.collapsed = payload
		},
		resetDockLayout: s => {
			s.dock.layout = null
			s.dock.placements = structuredClone(initialState.dock.placements)
			s.dock.collapsed = {}
		},
	},
})

// Actions

export const {
	clearConfirm,
	clearPrompt,
	closeError,
	collapsePanels,
	enforceKiosk,
	expandPanelsPartly,
	pushError,
	requestConfirm,
	requestPrompt,
	resetDockLayout,
	setCSSPortrait,
	setDialog,
	setDockCollapsed,
	setDockLayout,
	setDockPlacement,
	setShareDialog,
	setModal,
	setPage,
	setMouseDown,
	setPanelCollapsed,
	setPanelSize,
	setPanelZoom,
	setPending,
	setTextures,
	setPromptInput,
	setRename,
	setSettingsPage,
	setUIProperty,
	toggleFold,
	toggleUIProperty,
} = uiSlice.actions

// Selectors

export const selectPanelCollapsed = id => state => state.ui.panels?.collapsed?.[id] ?? false

export const selectCollapsedPanels = state => state.ui.panels?.collapsed ?? EMPTY_OBJECT

/** The param paths a partly expanded section shows; undefined when it shows all. */
export const selectPanelPartial = id => state => state.ui.panels?.partial?.[id]

export const selectPanelZoom = context => state => state.ui.panels?.zoom?.[context] ?? EMPTY_OBJECT

export const selectPanelSizes = state => state.ui.panels?.sizes ?? {}

export const selectDockLayout = state => state.ui.dock?.layout ?? null

export const selectDockPlacements = state => state.ui.dock?.placements ?? EMPTY_OBJECT

export const selectDockCollapsed = state => state.ui.dock?.collapsed ?? EMPTY_OBJECT

export const TOOLBAR_SIDES = ['left', 'right', 'top', 'bottom']

export const selectToolbarSide = state =>
	TOOLBAR_SIDES.includes(state.ui.dock?.toolbar_side) ? state.ui.dock.toolbar_side : 'top'

export const selectOpenPanelIds = createSelector(selectDockLayout, layout =>
	layout?.panels ? Object.keys(layout.panels) : EMPTY_ARRAY
)

// Thunks

export const confirmAction =
	(yes = true) =>
	dispatch => {
		dispatch({ type: 'ui/confirmAction', payload: { yes, no: !yes } })
	}

export const promptAction =
	(confirm = true) =>
	dispatch => {
		dispatch({ type: 'ui/promptAction', payload: { confirm } })
	}

export default uiSlice.reducer
